import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

import { logError } from 'services/logging/logging';
import { useForceDelivery } from 'services/repositories/transactions/transactions';
import { useCurrentUser } from 'services/repositories/user/user';
import { addNotification, NotificationType } from 'stores/notifications/notifications';
import { useAnalytics } from 'support/helpers/analytics/analytics';
import {
  ConfirmationModalIds,
  isForeverHiddenConfirmationModal,
} from 'support/helpers/confirmationModals/confirmationModals';
import { Connectors, ReceiverEnvelopeDTO } from 'support/types';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Display/Tooltip/Tooltip';
import { Button } from 'components/Form/Button/Button';

import { SubmitToErpConfirmationModal } from './components/SubmitToERPConfirmationModal/SubmitToErpConfirmationModal';

type ButtonProps = Parameters<typeof Button>[0];
export const SubmitToErpAction = ({
  numberOfDocuments,
  receiverEnvelope,
  size,
  variant,
  className,
}: { numberOfDocuments: number; receiverEnvelope: ReceiverEnvelopeDTO } & Pick<
  ButtonProps,
  'size' | 'variant' | 'className'
>) => {
  const { data: currentUser } = useCurrentUser();
  const { track } = useAnalytics();
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const { mutate: forceDelivery, isLoading: isSubmitting, isSuccess: isSubmitted } = useForceDelivery();

  const submitToErp = () => {
    track('payment_reconciliation:submit_to_erp.started', {
      connector: receiverEnvelope.receiverIntegration.connector,
    });
    forceDelivery(
      {
        receiverEnvelopeId: receiverEnvelope.id,
      },
      {
        onSuccess: () => {
          track('payment_reconciliation:submit_to_erp.successful', {
            connector: receiverEnvelope.receiverIntegration.connector,
          });
          const documentsTranslation = t('paymentReconciliation:submitToErp.notification.success.subtitle.documents', {
            count: numberOfDocuments,
          });
          const timeSavedTranslation = t('paymentReconciliation:submitToErp.notification.success.subtitle.timeSaved', {
            count: getSavedTime(numberOfDocuments),
          });
          addNotification({
            title: t('paymentReconciliation:submitToErp.notification.success.title'),
            subtitle: t('paymentReconciliation:submitToErp.notification.success.subtitle.main', {
              documents: documentsTranslation,
              timeSaved: timeSavedTranslation,
            }),
          });
        },
        onError: (error) => {
          track('payment_reconciliation:submit_to_erp.failed', {
            connector: receiverEnvelope.receiverIntegration.connector,
          });
          logError(error);
          addNotification(
            {
              title: t('paymentReconciliation:submitToErp.notification.error.title'),
              subtitle: t('common:error.generic'),
            },
            NotificationType.error,
          );
        },
      },
    );
  };

  return (
    <>
      <SubmitToErpConfirmationModal
        open={open}
        onConfirm={() => {
          setOpen(false);
          submitToErp();
        }}
        onCancel={() => setOpen(false)}
        connector={receiverEnvelope.receiverIntegration.connector}
      />
      <Tooltip enabled={!receiverEnvelope.canForceDeliver}>
        <TooltipTrigger>
          <Button
            size={size}
            disabled={!receiverEnvelope.canForceDeliver}
            className={className}
            loading={isSubmitting || isSubmitted} // We keep showing progress while we wait for the refetch of the queries to finish that will remove the button
            variant={variant}
            analyticsId={'paymentReconciliation:submitToErp'}
            onClick={(e) => {
              e.preventDefault();
              if (
                isForeverHiddenConfirmationModal(ConfirmationModalIds.SubmitToErpConfirmation, currentUser?.id || '')
              ) {
                submitToErp();
                return;
              }
              setOpen(true);
            }}
          >
            {getSubmitButtonLabel(receiverEnvelope, t)}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{getSubmitButtonTooltip(receiverEnvelope, t)}</TooltipContent>
      </Tooltip>
    </>
  );
};

const getSubmitButtonLabel = (receiverEnvelope: ReceiverEnvelopeDTO, t: TFunction) => {
  switch (receiverEnvelope.receiverIntegration.connector) {
    case Connectors.WECLAPP:
      return t('paymentReconciliation:submitToErp.weclapp.label');
    case Connectors.XENTRAL_V3:
      return t('paymentReconciliation:submitToErp.xentral.label');
    case Connectors.API_V2:
      return t('paymentReconciliation:submitToErp.api_v2.label');
    default:
      return t('paymentReconciliation:submitToErp.default.label');
  }
};

const getSubmitButtonTooltip = (receiverEnvelope: ReceiverEnvelopeDTO, t: TFunction) => {
  switch (receiverEnvelope.receiverIntegration.connector) {
    case Connectors.WECLAPP:
      return t('paymentReconciliation:submitToErp.weclapp.tooltip');
    case Connectors.XENTRAL_V3:
      return t('paymentReconciliation:submitToErp.xentral.tooltip');
    case Connectors.API_V2:
      return t('paymentReconciliation:submitToErp.api_v2.tooltip');
    default:
      return t('paymentReconciliation:submitToErp.default.tooltip');
  }
};

const getSavedTime = (numberOfDocuments: number): number => {
  const SECONDS_SAVED_PER_DOCUMENT = 33; // Patrick's estimation
  return Math.ceil((numberOfDocuments * SECONDS_SAVED_PER_DOCUMENT) / 60); // Convert seconds to minutes
};
